/**
 * The Knowledge page's decisions, kept out of the component so they can be
 * pinned by tests: which documents are the knowledge base, what a row says
 * about each file, and what happens to a batch of dropped files.
 *
 * @module components/regulation/knowledge/knowledgeBase.logic
 */
import {
  KNOWLEDGE_MAX_BYTES,
  fileExtension,
  knowledgeFileProblem,
  knowledgeFileType,
} from "@repo/shared/knowledgeFiles";

import { formatBytes, isKnowledgeDocument, policyFileVersions, policyVersion, type Policy } from "../../../state/policies";

export type KnowledgeDocument = Pick<
  Policy,
  | "id"
  | "filename"
  | "status"
  | "createdAt"
  | "uploadedByEmail"
  | "reference"
  | "sizeBytes"
  | "version"
  | "versionNote"
  | "versionUploadedAt"
  | "versionUploadedByEmail"
  | "fileVersions"
>;

/**
 * When the file in force arrived: its latest version's upload, else the
 * document's own (an older server, or a never-replaced file).
 */
export const knowledgeUpdatedAt = (document: Pick<KnowledgeDocument, "createdAt" | "versionUploadedAt">): string =>
  document.versionUploadedAt ?? document.createdAt;

/** Who put the file in force there: the latest version's uploader, else the original one. */
export const knowledgeUpdatedBy = (
  document: Pick<KnowledgeDocument, "uploadedByEmail" | "versionUploadedByEmail">,
): string | null => document.versionUploadedByEmail ?? document.uploadedByEmail;

/** The knowledge base, most recently updated first: what just changed is what a person looks for. */
export const knowledgeDocuments = <T extends KnowledgeDocument>(
  documents: ReadonlyArray<T>,
): ReadonlyArray<T> =>
  documents
    .filter(isKnowledgeDocument)
    .toSorted((a, b) => (Date.parse(knowledgeUpdatedAt(b)) || 0) - (Date.parse(knowledgeUpdatedAt(a)) || 0));

export interface KnowledgeVersionEntry {
  readonly version: number;
  readonly filename: string;
  readonly uploadedAt: string;
  readonly uploadedByEmail: string | null;
  readonly note: string | null;
  /** The file eve searches now; every other entry is history. */
  readonly current: boolean;
}

/** Every version of a file, newest first, the one in force at the top. */
export const knowledgeVersionHistory = (document: KnowledgeDocument): ReadonlyArray<KnowledgeVersionEntry> => [
  {
    version: policyVersion(document),
    filename: document.filename,
    uploadedAt: knowledgeUpdatedAt(document),
    uploadedByEmail: knowledgeUpdatedBy(document),
    note: document.versionNote ?? null,
    current: true,
  },
  ...policyFileVersions(document)
    .toReversed()
    .map((entry) => ({ ...entry, current: false })),
];

/** What kind of file it is, for the Type column: "PDF", "Word", "Markdown". */
export const knowledgeTypeLabel = (filename: string): string => {
  const known = knowledgeFileType(filename);
  if (known) return known.label;
  // Eve can file a document under a name outside the upload table; say what
  // it is called rather than pretend it is one of ours.
  const extension = fileExtension(filename);
  return extension ? extension.slice(1).toUpperCase() : "File";
};

/** A PDF opens in the browser's viewer; every other type downloads. */
export const opensInline = (filename: string): boolean => fileExtension(filename) === ".pdf";

/** The Size column; "—" for a file filed before sizes were recorded. */
export const knowledgeSizeLabel = (document: Pick<KnowledgeDocument, "sizeBytes">): string =>
  document.sizeBytes === undefined ? "—" : formatBytes(document.sizeBytes);

/** Who added it, as a name a person recognises: the email's local part. */
export const uploaderLabel = (email: string | null): string | null => {
  const trimmed = email?.trim() ?? "";
  if (trimmed.length === 0) return null;
  const at = trimmed.indexOf("@");
  return at > 0 ? trimmed.slice(0, at) : trimmed;
};

/** Search over the file name, and its type label ("word", "markdown"). */
export const matchesKnowledgeQuery = (document: Pick<KnowledgeDocument, "filename">, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return (
    document.filename.toLowerCase().includes(needle) ||
    knowledgeTypeLabel(document.filename).toLowerCase().includes(needle)
  );
};

/** Processing older than this is a dead job, not work in flight (as on the Policies page). */
export const KNOWLEDGE_STALL_MS = 24 * 60 * 60 * 1000;

export type KnowledgeStatus = "ready" | "processing" | "stalled" | "failed";

export const knowledgeStatus = (
  document: Pick<KnowledgeDocument, "status" | "createdAt" | "versionUploadedAt">,
  now: number,
): KnowledgeStatus => {
  if (document.status !== "processing") return document.status;
  // A new version restarts processing; the clock starts when it arrived.
  const started = Date.parse(knowledgeUpdatedAt(document));
  return !Number.isNaN(started) && now - started > KNOWLEDGE_STALL_MS ? "stalled" : "processing";
};

/**
 * Whether the page should keep re-reading the list: only while a file is
 * genuinely in flight. A stalled job will not finish by being polled.
 */
export const knowledgeNeedsPolling = (
  documents: ReadonlyArray<Pick<KnowledgeDocument, "status" | "createdAt" | "versionUploadedAt">>,
  now: number,
): boolean => documents.some((document) => knowledgeStatus(document, now) === "processing");

export interface RejectedFile {
  readonly name: string;
  readonly reason: string;
}

export interface KnowledgeUploadPlan<F> {
  /** New files: each becomes a document of its own. */
  readonly accepted: ReadonlyArray<F>;
  /** Files named like one already in the knowledge base: each becomes its next version. */
  readonly versions: ReadonlyArray<{ readonly file: F; readonly documentId: string }>;
  readonly rejected: ReadonlyArray<RejectedFile>;
}

const sameName = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Shown when a new version arrives while the last one is still being read. */
export const VERSION_WHILE_PROCESSING = "The previous version is still processing — try again in a moment";
/** A run that died still holds the document; the API would refuse the version too. */
export const VERSION_WHILE_STALLED = "Its last version never finished processing — remove it, then upload this again";
/** A version is already on its way up; a second one now would race it. */
export const VERSION_ALREADY_UPLOADING = "A new version of this file is already uploading — add this one once it finishes";
/** Two different files under one name would become two documents eve cites separately. */
export const NAME_ALREADY_UPLOADING = "Another file with this name is already uploading — add this one once it finishes";

export interface KnowledgeUploadContext {
  /** New files still uploading from an earlier drop. */
  readonly inFlight?: ReadonlyArray<{ readonly name: string; readonly size: number }>;
  /** The knowledge base as it stands, newest first. */
  readonly existing?: ReadonlyArray<Pick<KnowledgeDocument, "id" | "filename" | "status" | "createdAt" | "versionUploadedAt">>;
  /** Documents with a new version on its way up. */
  readonly versionsInFlight?: ReadonlySet<string>;
  readonly now?: number;
}

/**
 * Sort a drop into what will upload and what will not, and why. The same
 * file twice in one drop (or one already uploading) is taken once — a
 * double drop must not file two copies eve would then cite twice. A file
 * named like one already here (`existing`, newest first) is that file's next
 * version, not a second copy: re-uploading the updated handbook replaces
 * what eve searches and keeps the old one in its history.
 */
export const planKnowledgeUploads = <F extends { readonly name: string; readonly size: number }>(
  files: ReadonlyArray<F>,
  { inFlight = [], existing = [], versionsInFlight = new Set(), now = Date.now() }: KnowledgeUploadContext = {},
): KnowledgeUploadPlan<F> => {
  // By name, not just name and size: two different files called
  // "Handbook.pdf" are one document's versions, never two documents.
  const keyByName = new Map(inFlight.map((file) => [file.name.trim().toLowerCase(), uploadKey(file)] as const));
  const accepted: F[] = [];
  const versions: Array<{ file: F; documentId: string }> = [];
  const rejected: RejectedFile[] = [];
  const refusedKeys = new Set<string>();
  for (const file of files) {
    const key = uploadKey(file);
    // Validated before it claims its name: a refused file must not block a
    // good one of the same name later in the drop.
    const problem = knowledgeFileProblem(file);
    if (problem !== null) {
      if (!refusedKeys.has(key)) rejected.push({ name: file.name, reason: problem });
      refusedKeys.add(key);
      continue;
    }
    const name = file.name.trim().toLowerCase();
    const earlier = keyByName.get(name);
    // The very same file twice is a double drop: take it once, say nothing.
    if (earlier === key) continue;
    if (earlier !== undefined) {
      rejected.push({ name: file.name, reason: NAME_ALREADY_UPLOADING });
      continue;
    }
    keyByName.set(name, key);
    const target = existing.find((document) => sameName(document.filename, file.name));
    if (target === undefined) {
      accepted.push(file);
      continue;
    }
    if (versionsInFlight.has(target.id)) {
      rejected.push({ name: file.name, reason: VERSION_ALREADY_UPLOADING });
      continue;
    }
    // The API refuses a replacement mid-run; say so before sending 25 MB.
    const status = knowledgeStatus(target, now);
    if (status === "processing") rejected.push({ name: file.name, reason: VERSION_WHILE_PROCESSING });
    else if (status === "stalled") rejected.push({ name: file.name, reason: VERSION_WHILE_STALLED });
    else versions.push({ file, documentId: target.id });
  }
  return { accepted, versions, rejected };
};

/** One file's identity within a drop, for de-duplication. */
export const uploadKey = (file: { readonly name: string; readonly size: number }): string =>
  `${file.name}:${file.size}`;

export const KNOWLEDGE_MAX_MB = KNOWLEDGE_MAX_BYTES / (1024 * 1024);
